import { resolveCapsuleThumbnailMedia } from "./timeCapsuleThumbnail";

const GEM_KIND_ORDER = {
  sponsored: 0,
  memberExclusive: 1,
  adminStarred: 2,
  loved: 3,
  received: 4,
};

const DEFAULT_LOVED_NEARBY_RADIUS_METERS = 15000;

function toCount(value) {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
}

function formatCount(count, singular, plural) {
  return `${count > 99 ? "99+" : count} ${count === 1 ? singular : plural}`;
}

function isUsLocale(locale = "") {
  return String(locale || "").toLowerCase().endsWith("-us");
}

export function formatNearbyDistance(distanceMetersValue, locale = "") {
  const meters = Number(distanceMetersValue);
  if (!Number.isFinite(meters)) return "";

  if (isUsLocale(locale)) {
    const feet = meters * 3.28084;
    if (feet < 528) return `${Math.max(25, Math.round(feet / 25) * 25)} ft`;
    const miles = meters / 1609.344;
    return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
  }

  if (meters < 1000) return `${Math.max(1, Math.round(meters / 25) * 25)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

export function buildNearbyDiscoveryCtaLabel({ offersCount = 0, gemsCount = 0 } = {}) {
  const offers = toCount(offersCount);
  const gems = toCount(gemsCount);
  if (offers > 0 && gems > 0) {
    return `${formatCount(offers, "offer", "offers")} + ${formatCount(gems, "gem", "gems")} nearby`;
  }
  if (offers > 0) return `${formatCount(offers, "offer", "offers")} nearby`;
  if (gems > 0) return `${formatCount(gems, "gem", "gems")} nearby`;
  return "";
}

export function getNearbyDiscoverySheetTitle({ offersCount = 0, gemsCount = 0 } = {}) {
  const offers = toCount(offersCount);
  const gems = toCount(gemsCount);
  if (offers > 0 && gems > 0) return "Nearby deals and gems";
  if (offers > 0) return "Offers nearby";
  if (gems > 0) return "Gems nearby";
  return "Nearby";
}

export function shouldLoadNearbyDiscovery({ offerCenterKey = "", offerQueryCenter = null } = {}) {
  return Boolean(String(offerCenterKey || "").trim() && offerQueryCenter);
}

function fulfilledArray(result = {}) {
  return result?.status === "fulfilled" && Array.isArray(result.value) ? result.value : [];
}

function safeDecodeURIComponent(value = "") {
  try {
    return decodeURIComponent(value);
  } catch (_) {
    return String(value || "");
  }
}

function resolveInternalPath(value = "") {
  const text = firstString(value);
  if (!text) return "";

  try {
    return new URL(text, "https://inssa.us").pathname;
  } catch (_) {
    const match = text.match(/\/(?:coupons|raffles)\/c\/[^?#\s]+/i);
    return match?.[0] || "";
  }
}

export function resolveClaimPathOffer(value = "") {
  const pathname = resolveInternalPath(value);
  const match = pathname.match(/^\/(coupons|raffles)\/c\/([^/]+)\/?$/i);
  if (!match) return null;

  const campaignType = match[1].toLowerCase() === "coupons" ? "coupon" : "raffle";
  const campaignId = safeDecodeURIComponent(match[2]);
  if (!campaignId) return null;

  return {
    campaignId,
    campaignType,
    publicClaimPath: `/${campaignType === "coupon" ? "coupons" : "raffles"}/c/${match[2]}`,
  };
}

function resolveLinkedOfferClaim(gem = {}) {
  const raw = gem?.raw || {};
  const candidates = [
    raw?.stableClaimPath,
    raw?.publicClaimPath,
    raw?.stableClaimUrl,
    raw?.publicClaimUrl,
    raw?.callToActionUrl,
    gem?.callToActionUrl,
  ];

  for (const candidate of candidates) {
    const claim = resolveClaimPathOffer(candidate);
    if (claim) return claim;
  }

  return null;
}

function resolveLinkedOfferTitle(gem = {}, claim = {}) {
  const raw = gem?.raw || {};
  const explicitTitle = firstString(
    raw?.campaignTitle,
    raw?.offerTitle,
    raw?.messageTitle,
    raw?.headline,
    raw?.title,
    raw?.name,
    raw?.couponTitle,
    raw?.raffleTitle
  );
  if (explicitTitle) return explicitTitle;

  const bodyText = firstString(raw?.messageBody, raw?.body, raw?.description);
  const gemTitle = firstString(gem?.title);
  if (gemTitle && gemTitle !== bodyText) return gemTitle;

  return claim?.campaignType === "coupon" ? "Coupon offer" : "Raffle entry";
}

function buildOfferFromLinkedGem(gem = {}) {
  const claim = resolveLinkedOfferClaim(gem);
  if (!claim) return null;

  const raw = gem?.raw || {};
  return {
    id: firstString(gem?.id, raw?.id, raw?.capsuleId, claim.campaignId),
    campaignId: firstString(raw?.campaignId, raw?.raffleId, claim.campaignId),
    campaignType: claim.campaignType,
    businessId: firstString(raw?.businessId, raw?.senderId),
    title: resolveLinkedOfferTitle(gem, claim),
    heroImageUrl: firstString(gem?.imageUrl, raw?.heroImageUrl, raw?.thumbnailUrl, raw?.imageUrl),
    claimStartAt: raw?.claimStartAt || null,
    claimEndAt: raw?.claimEndAt || raw?.endDate || null,
    locationId: firstString(raw?.locationId),
    locationName: firstString(gem?.locationLabel, raw?.locationName, raw?.placeName, raw?.seedName),
    address: firstString(raw?.address, raw?.location?.address),
    lat: raw?.location?.lat ?? raw?.lat ?? null,
    lng: raw?.location?.lng ?? raw?.lng ?? null,
    geohash: firstString(raw?.geohash),
    publicClaimPath: claim.publicClaimPath,
    publicClaimUrl: firstString(raw?.publicClaimUrl, raw?.stableClaimUrl, raw?.callToActionUrl),
    distanceMeters: Number.isFinite(Number(gem?.distanceMeters)) ? Number(gem.distanceMeters) : null,
    source: "timeCapsule",
    // Keep the driving capsule so the drawer can measure the capsule funnel
    // (impression/open) and, later, open the capsule detail instead of the bare
    // claim page. Null for studio offers, which have no anchoring capsule.
    capsule: raw && typeof raw === "object" ? raw : null,
  };
}

function getOfferDedupeKey(offer = {}) {
  const campaignType = firstString(offer?.campaignType);
  const campaignId = firstString(offer?.campaignId);
  return firstString(
    offer?.publicClaimPath,
    offer?.stableClaimPath,
    campaignType && campaignId ? `${campaignType}:${campaignId}` : "",
    offer?.id
  );
}

function mergeNearbyOffers(offers = []) {
  const byKey = new Map();
  offers.forEach((offer) => {
    const key = getOfferDedupeKey(offer);
    if (!key) return;
    if (!byKey.has(key)) {
      byKey.set(key, offer);
      return;
    }

    const existing = byKey.get(key);
    byKey.set(key, {
      ...offer,
      ...existing,
      heroImageUrl: existing?.heroImageUrl || offer?.heroImageUrl || "",
      distanceMeters: Number.isFinite(Number(existing?.distanceMeters))
        ? Number(existing.distanceMeters)
        : offer?.distanceMeters ?? null,
    });
  });
  return Array.from(byKey.values());
}

export function resolveNearbyDiscoveryResults({ offerResult = {}, gemResult = {} } = {}) {
  const linkedOffers = [];
  const gems = [];
  fulfilledArray(gemResult).forEach((gem) => {
    const linkedOffer = buildOfferFromLinkedGem(gem);
    if (linkedOffer) {
      linkedOffers.push(linkedOffer);
      return;
    }
    gems.push(gem);
  });

  const offers = mergeNearbyOffers([...fulfilledArray(offerResult), ...linkedOffers]);
  const hasAnyFailure = offerResult?.status === "rejected" || gemResult?.status === "rejected";
  const hasAnySuccess = offerResult?.status === "fulfilled" || gemResult?.status === "fulfilled";
  return { offers, gems, hasAnyFailure, hasAnySuccess };
}

function normalizeCapsuleType(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[-_\s]/g, "");
}

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function getLatLngFromCapsule(capsule = {}) {
  const candidates = [
    capsule?.location,
    capsule?.position,
    capsule,
  ];

  for (const candidate of candidates) {
    const lat = toFiniteNumber(candidate?.lat);
    const lng = toFiniteNumber(candidate?.lng);
    if (
      lat != null &&
      lng != null &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180 &&
      !(lat === 0 && lng === 0)
    ) {
      return { lat, lng };
    }
  }

  return null;
}

function distanceMetersBetween(a, b) {
  const left = getLatLngFromCapsule(a);
  const right = getLatLngFromCapsule(b);
  if (!left || !right) return Number.POSITIVE_INFINITY;

  const toRad = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMeters = 6371000;
  const dLat = toRad(right.lat - left.lat);
  const dLng = toRad(right.lng - left.lng);
  const leftLat = toRad(left.lat);
  const rightLat = toRad(right.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(leftLat) * Math.cos(rightLat) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function getNearbyGemKind(capsule = {}, viewerUid = "") {
  const capsuleType = normalizeCapsuleType(capsule?.capsuleType || capsule?.type);
  if (capsuleType === "sponsored") return "sponsored";
  if (capsuleType === "memberexclusive") return "memberExclusive";
  if (capsule?.adminStarred === true || capsule?.insider === true) return "adminStarred";

  const uid = String(viewerUid || "").trim();
  const recipientUserIds = Array.isArray(capsule?.recipientUserIds)
    ? capsule.recipientUserIds.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  const senderId = String(capsule?.senderId || "").trim();
  if (capsuleType === "private" && uid && recipientUserIds.includes(uid) && senderId !== uid) {
    return "received";
  }

  return "";
}

function firstString(...values) {
  return values.map((value) => String(value || "").trim()).find(Boolean) || "";
}

export function normalizeNearbyGem(capsule = {}, { viewerUid = "", distanceMeters = null } = {}) {
  const id = firstString(capsule?.id, capsule?.capsuleId);
  const gemKind = getNearbyGemKind(capsule, viewerUid);
  if (!id || !gemKind) return null;

  const thumbnailMedia = resolveCapsuleThumbnailMedia(capsule);
  const imageUrl = firstString(
    capsule?.heroImageUrl,
    capsule?.thumbnailUrl,
    capsule?.imageUrl,
    capsule?.image,
    capsule?.posterUrl,
    thumbnailMedia.url
  );

  return {
    id,
    title: firstString(capsule?.title, capsule?.headline, capsule?.seedName, capsule?.placeName, capsule?.messageBody, "Time capsule"),
    imageUrl,
    thumbnailSourceMediaType: thumbnailMedia.sourceMediaType || "",
    businessAvatarUrl: firstString(
      capsule?.businessAvatarUrl,
      capsule?.pinUrl,
      capsule?.business?.pinUrl,
      capsule?.business?.avatarUrl,
      capsule?.business?.logoUrl
    ),
    senderAvatarUrl: firstString(
      capsule?.senderAvatarUrl,
      capsule?.senderProfileImage,
      capsule?.senderPhotoURL,
      capsule?.senderPhotoUrl
    ),
    distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
    gemKind,
    capsuleType: String(capsule?.capsuleType || capsule?.type || "").trim(),
    senderLabel: firstString(
      capsule?.senderDisplayName,
      capsule?.senderPublicHandle ? `@${String(capsule.senderPublicHandle).replace(/^@+/, "")}` : "",
      capsule?.senderName,
      capsule?.senderUserName,
      capsule?.businessName,
      capsule?.placeName,
      capsule?.seedName
    ),
    locationLabel: firstString(capsule?.locationName, capsule?.address, capsule?.placeName, capsule?.seedName),
    placeLabel: firstString(capsule?.locationName, capsule?.placeName, capsule?.seedName),
    address: firstString(capsule?.location?.address, capsule?.address),
    path: `/capsule/${encodeURIComponent(id)}`,
    raw: capsule,
  };
}

export function sortNearbyGems(gems = []) {
  return [...gems].sort((a, b) => {
    const leftOrder = GEM_KIND_ORDER[a?.gemKind] ?? 99;
    const rightOrder = GEM_KIND_ORDER[b?.gemKind] ?? 99;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return Number(a?.distanceMeters || Infinity) - Number(b?.distanceMeters || Infinity);
  });
}

function normalizeLovedNearbyGem(capsule = {}, { distanceMeters = null } = {}) {
  const id = firstString(capsule?.id, capsule?.capsuleId);
  if (!id) return null;

  const thumbnailMedia = resolveCapsuleThumbnailMedia(capsule);
  const imageUrl = firstString(
    capsule?.heroImageUrl,
    capsule?.thumbnailUrl,
    capsule?.imageUrl,
    capsule?.image,
    capsule?.posterUrl,
    thumbnailMedia.url
  );

  return {
    id,
    title: firstString(
      capsule?.title,
      capsule?.headline,
      capsule?.messageTitle,
      capsule?.seedName,
      capsule?.placeName,
      capsule?.messageBody,
      "Time capsule"
    ),
    imageUrl,
    thumbnailSourceMediaType: thumbnailMedia.sourceMediaType || "",
    distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
    gemKind: "loved",
    capsuleType: String(capsule?.capsuleType || capsule?.type || "").trim(),
    senderLabel: firstString(
      capsule?.senderDisplayName,
      capsule?.senderPublicHandle ? `@${String(capsule.senderPublicHandle).replace(/^@+/, "")}` : "",
      capsule?.senderName,
      capsule?.senderUserName,
      capsule?.businessName
    ),
    locationLabel: firstString(capsule?.locationName, capsule?.address, capsule?.placeName, capsule?.seedName),
    placeLabel: firstString(capsule?.locationName, capsule?.placeName, capsule?.seedName),
    address: firstString(capsule?.location?.address, capsule?.address),
    path: `/capsule/${encodeURIComponent(id)}`,
    raw: capsule,
  };
}

export function mergeLovedNearbyGems({
  gems = [],
  lovedCapsules = [],
  center = null,
  radiusMeters = DEFAULT_LOVED_NEARBY_RADIUS_METERS,
} = {}) {
  const baseGems = Array.isArray(gems) ? gems.filter(Boolean) : [];
  const existingGemIndexById = new Map();
  baseGems.forEach((gem, index) => {
    const id = String(gem?.id || "").trim();
    if (id) existingGemIndexById.set(id, index);
  });
  const existingIds = new Set(existingGemIndexById.keys());
  const finalRadius = Math.max(1, Number(radiusMeters) || DEFAULT_LOVED_NEARBY_RADIUS_METERS);
  const centerPoint = getLatLngFromCapsule(center);
  if (!centerPoint) return sortNearbyGems(baseGems);

  const lovedGems = (Array.isArray(lovedCapsules) ? lovedCapsules : [])
    .map((capsule) => {
      const id = firstString(capsule?.id, capsule?.capsuleId);
      if (!id) return null;
      const capsulePoint = getLatLngFromCapsule(capsule);
      if (!capsulePoint) return null;
      const distMeters = distanceMetersBetween(centerPoint, capsulePoint);
      if (!Number.isFinite(distMeters) || distMeters > finalRadius) return null;
      if (existingIds.has(id)) {
        const existingIndex = existingGemIndexById.get(id);
        if (existingIndex != null) {
          baseGems[existingIndex] = {
            ...baseGems[existingIndex],
            isLovedByViewer: true,
          };
        }
        return null;
      }
      const gem = normalizeLovedNearbyGem(capsule, { distanceMeters: distMeters });
      if (gem) existingIds.add(gem.id);
      return gem;
    })
    .filter(Boolean);

  return sortNearbyGems([...baseGems, ...lovedGems]);
}

export function groupNearbyGemsBySection(gems = []) {
  const discoverGems = [];
  const lovedGems = [];
  const friendGems = [];

  (Array.isArray(gems) ? gems : []).forEach((gem) => {
    const isLovedByViewer = gem?.gemKind === "loved" || gem?.isLovedByViewer === true;
    if (isLovedByViewer) {
      lovedGems.push(gem);
    }

    if (gem?.gemKind === "received") {
      friendGems.push(gem);
      return;
    }
    if (gem?.gemKind === "loved") {
      return;
    }
    discoverGems.push(gem);
  });

  return [
    discoverGems.length ? { key: "gems", label: "Gems", items: discoverGems } : null,
    lovedGems.length ? { key: "loved", label: "Loved by you", items: lovedGems } : null,
    friendGems.length ? { key: "friends", label: "From my friends", items: friendGems } : null,
  ].filter(Boolean);
}

export function getNearbyShortcutState(gems = []) {
  return {
    showFromContacts: gems.some((gem) => gem?.gemKind === "received"),
    showLoved: gems.some((gem) => gem?.gemKind === "loved" || gem?.isLovedByViewer === true),
    showMemberExclusives: gems.some((gem) => gem?.gemKind === "memberExclusive"),
  };
}

export function getNearbyContactShortcutLabel() {
  return "From my friends";
}

export function getNearbyLovedShortcutLabel() {
  return "Loved by you";
}

export function getNearbyGemKindLabel(gem = {}) {
  switch (gem?.gemKind) {
    case "sponsored":
      return "Featured";
    case "memberExclusive":
      return "Exclusive";
    case "adminStarred":
      return "Inssa Gem";
    case "loved":
      return "Loved";
    case "received": {
      const senderLabel = firstString(gem?.senderLabel);
      return senderLabel ? `From ${senderLabel}` : "From contact";
    }
    default:
      return "Gem";
  }
}

export function getNearbyGemThumbnailUrl(
  gem = {},
  { memberExclusiveFallbackUrl = "", isGated = false } = {}
) {
  // The fallback wins outright when gated — not merely when imageUrl is empty,
  // which is what let hero images through.
  if (isGated) return firstString(memberExclusiveFallbackUrl);

  const imageUrl = firstString(gem?.imageUrl);
  if (imageUrl) return imageUrl;
  if (gem?.gemKind === "received") return firstString(gem?.senderAvatarUrl);
  if (gem?.gemKind === "sponsored") return firstString(gem?.businessAvatarUrl);
  if (gem?.gemKind === "memberExclusive") return firstString(memberExclusiveFallbackUrl);
  return "";
}

export function getNearbyGemThumbnailFallbackKind(gem = {}) {
  if (firstString(gem?.imageUrl)) return "";
  if (gem?.thumbnailSourceMediaType === "video") return "video";
  return "";
}
